import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";
import { selectScopedJobIds } from "../../../redux/slices/job-scope-slice";
import StageEmptyState from "./StageEmptyState";
import RecommendationBadge from "../../../components/shared/RecommendationBadge";
import LoadingState from "../../../components/shared/LoadingState";
import ErrorState from "../../../components/shared/ErrorState";

const scoreTone = (v) => {
  if (v == null) return "text-slate-400";
  const n = +v;
  const t10 = n <= 10;
  if (t10 ? n >= 8 : n >= 80) return "text-emerald-600 font-semibold";
  if (t10 ? n >= 6 : n >= 60) return "text-amber-600";
  return "text-red-600";
};

const fmtScore = (v) => {
  if (v == null) return "—";
  const n = +v;
  return (n <= 10 ? n.toFixed(1) : n.toFixed(1));
};

export default function StageList({ stage, title, subtitle, actions = [], emptyCta }) {
  const scope = useSelector(selectScopedJobIds);
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  useEffect(() => { document.title = `${title} - Ample Hire`; }, [title]);

  const load = () => {
    const jobIds = scope.length ? `&jobIds=${scope.join(",")}` : "";
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    axiosInstance
      .get(`${baseUrl}/job-posting/pipeline?stage=${stage}${jobIds}`)
      .then((r) => setRows(r.data?.candidates || []))
      .catch((e) => {
        setError(e?.message || "Failed to load candidates");
        // Pull status off the wrapped axios error (interceptors throw a
        // bare object with a status field on 403, otherwise the standard
        // AxiosError.response.status is present).
        setErrorStatus(e?.status || e?.response?.status || 500);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [scope.join(","), stage]);

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">{title}</h1>
          <p className="text-sm text-vf-muted mt-1">
            {subtitle}
            {!loading && !error && rows.length > 0 && (
              <span className="ml-2 text-vf-muted">
                · {rows.length} {rows.length === 1 ? "candidate" : "candidates"}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="px-3 py-1.5 rounded border border-slate-300 text-xs hover:bg-vf-tint/30"
          >
            Refresh
          </button>
          <button
            onClick={() => nav("/admin/pipeline")}
            className="px-3 py-1.5 rounded border border-slate-300 text-xs"
          >
            ← Pipeline
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <LoadingState variant="skeleton" shape="row" count={4} />
        </div>
      ) : error ? (
        <ErrorState
          status={errorStatus}
          description={errorStatus === 403 ? undefined : error}
          retry={load}
        />
      ) : rows.length === 0 ? (
        <StageEmptyState stage={stage} emptyCta={emptyCta} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-vf-tint/50">
              <tr className="text-left text-xs text-vf-muted uppercase">
                <th className="py-2.5 px-4">Candidate</th>
                <th className="py-2.5">Position</th>
                <th className="py-2.5">Score</th>
                <th className="py-2.5">Stage</th>
                <th className="py-2.5 text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={`${c.userId}-${c.jobId}`} className="border-t border-slate-100 hover:bg-vf-tint/20">
                  <td className="py-2.5 px-4">
                    <button
                      onClick={() => nav(`/admin/candidate-report?candidateId=${c.userId}`)}
                      className="text-vf-primary hover:underline font-medium"
                    >
                      {c.name || `Candidate ${c.userId}`}
                    </button>
                    {c.email && <div className="text-xs text-vf-muted">{c.email}</div>}
                  </td>
                  <td className="py-2.5 text-vf-muted">{c.position || `Job ${c.jobId}`}</td>
                  <td className={`py-2.5 ${scoreTone(c.score)}`}>
                    <div className="flex items-center gap-2">
                      <span>{fmtScore(c.score)}</span>
                      <RecommendationBadge score={c.score} size="sm" />
                    </div>
                  </td>
                  <td className="py-2.5">
                    <span className="px-2 py-0.5 rounded text-xs bg-vf-tint/60 text-vf-base">
                      {STAGE_LABELS[c.rawStatusCode] || `#${c.rawStatusCode}`}
                    </span>
                  </td>
                  <td className="py-2.5 text-right pr-4 whitespace-nowrap">
                    {actions.map((a) => (
                      <button
                        key={a.label}
                        onClick={async () => {
                          try { await a.onClick(c); load(); }
                          catch (e) { console.error(a.label, "failed", e); }
                        }}
                        className={`ml-2 text-xs hover:underline ${a.danger ? "text-red-600" : "text-vf-primary"}`}
                      >
                        {a.label}
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const STAGE_LABELS = {
  3: "R1 Shortlisted",
  7: "R2 Shortlisted",
  10: "L3 Shortlisted",
  12: "Offered",
  14: "Joined",
  17: "HR Can Consider",
  18: "HR Shortlisted",
  21: "L1 Can Consider",
  22: "L1 Shortlisted",
  24: "L2 Shortlisted",
  26: "L3 Shortlisted",
  28: "Offered",
  30: "Joined",
};

